/* eslint-disable @typescript-eslint/no-explicit-any */
import React, {useEffect, useState, useMemo} from 'react';
import {images} from '../../../../constants/image';
import DialogContent from '@mui/material/DialogContent';
import {Checkbox, FormControl, Grid} from '@mui/material';
import RichTextEditor from '../../../../components/RichTextEditor/RichTextEditor';
import {Formik} from 'formik';
import * as Yup from 'yup';

import {
  Modal,
  Button,
  Input,
  DateTimePicker,
  TextArea,
  RadioGroups,
  MultipleSelectCheckbox,
  Tooltips,
  AspectSelectImage,
} from '../../../../components';
import useAuth from '../../../../hooks/useAuth';
import {formatDateTime} from '../../../../utils';
import SourceOutlinedIcon from '@mui/icons-material/SourceOutlined';
import {
  createNewVouchers,
  onChangeEditor,
  viewSourceHtmlEditor,
  ticketUsageTypeRadioOptions,
  radioTicketVisibilityOptions,
  TicketUsageTypeEnums,
  TicketAddNewDefaultParams,
  limitQuantity,
} from '../../../../utils/voucher/VoucherUtils';
import {getIslanderPartners} from '../../../../utils/islander-partners/IslanderPartners';
import {getVoucherCategory} from '../../../../utils/voucher-categories/voucher-categories';

// styles
import {
  Typography,
  StyledIconClose,
  StyledModalTitle,
  StyledModalActions,
  Divider,
} from './ModalAddNewVoucher.style';

interface ModalAddNewProps {
  visible: boolean;
  onClose: () => void;
  reloadPage: () => void;
}

interface DateTimeRange {
  startOfDateTime?: Date | null;
  endOfDateTime?: Date | null;
}

function ModalAddNewVoucher({
  visible,
  onClose = () => {},
  reloadPage = () => {},
}: ModalAddNewProps) {
  const {showNotification} = useAuth();
  const formatTime = 'DD/MM/YYYY HH:mm';
  const [valueRangePicker, setValueRangePicker] = useState<DateTimeRange>({
    startOfDateTime: null,
    endOfDateTime: null,
  });

  // states
  const [initialValues, setInitialValues] = useState(TicketAddNewDefaultParams);

  const [isLoading, setIsLoading] = useState(false);
  const [optionIslandPartners, setOptionIslandPartners] = useState([]);
  const [islandPartners, setOnChangeMultipleSelect] = useState([]);
  const [optionVoucherCategory, setOptionVoucherCategory] = useState([]);
  const [categorySelectCheckbox, setCategorySelectCheckbox] = useState([]);
  const [defaultValue, setDefaultValue] = useState('');
  const selectIPPlaceholder = useMemo(() => {
    return Array.isArray(islandPartners) && islandPartners.length > 0
      ? 'Select IP'
      : 'All IP';
  }, [islandPartners]);
  const selectCategoryPlaceholder = useMemo(() => {
    return Array.isArray(categorySelectCheckbox) &&
      categorySelectCheckbox.length > 0
      ? 'Select Categories'
      : 'All';
  }, [categorySelectCheckbox]);
  const [disabledQty, setDisabledQty] = useState(false);
  const [voucherImages, setVoucherImages] = useState([]);

  const addNewVoucher = async values => {
    setIsLoading(true);
    const payload = {
      ...values,
      expireStartDateTime: formatDateTime(
        new Date(values.startOfDateTime),
        formatTime
      ),
      expireEndDateTime: formatDateTime(
        new Date(values.endOfDateTime),
        formatTime
      ),
      merchantIds: islandPartners.map(ip => ip.value),
      ticketCategoryIds: categorySelectCheckbox.map(tc => tc.value),
      isCashoutAllowed: values.isCashoutAllowed ? 1 : 0,
    };
    const res = await createNewVouchers(payload, voucherImages);
    if (res.success && res.code === 200) {
      showNotification({
        message: res.message,
      });
    } else {
      showNotification({
        message: res.message,
        variation: 'error',
      });
    }
    if (res.isReloadPage) reloadPage();
    if (res.isClose) onCloseModal();
    setIsLoading(false);
  };

  const handleMappingIslanderPartners = async () => {
    const result = await getIslanderPartners();
    setOptionIslandPartners(result);
  };

  const handleMappingVoucherCategoryOptions = async () => {
    const result = await getVoucherCategory();
    setOptionVoucherCategory(result);
  };

  const onClean = async () => {
    setInitialValues(TicketAddNewDefaultParams);

    setValueRangePicker({startOfDateTime: null, endOfDateTime: null});
    setCategorySelectCheckbox([]);
    setOptionIslandPartners([]);
    setDefaultValue('');
    setVoucherImages([]);
  };

  const onCloseModal = async () => {
    await onClean();
    onClose();
  };

  const onClickSource = (data: string) => {
    const editorString = viewSourceHtmlEditor(data, defaultValue);
    setDefaultValue(editorString);
  };

  const onChange = (
    e: any,
    values: any,
    handleChange: (content: any) => {}
  ) => {
    const result = onChangeEditor(e, values, handleChange);
    setInitialValues(result);
  };

  useEffect(() => {
    setDisabledQty(initialValues.unlimitedQuantity);
  }, [initialValues.unlimitedQuantity]);

  useEffect(() => {
    if (visible) {
      handleMappingIslanderPartners();
      handleMappingVoucherCategoryOptions();
    }
  }, [visible]);

  return (
    <Modal visible={visible} onClose={onCloseModal} maxWidth="md">
      <>
        <StyledModalTitle>Add New Voucher</StyledModalTitle>
        <StyledIconClose onClick={onCloseModal}>
          <img src={images.icon_close} />
        </StyledIconClose>
        <Divider variant="middle" />
        <DialogContent>
          <Formik
            initialValues={initialValues}
            enableReinitialize
            validationSchema={Yup.object().shape({
              title: Yup.string().required('Voucher Title is required').max(40),
              excerpt: Yup.string()
                .required('Voucher Short Description is required')
                .max(85, 'Excerpt must be at most 85 characters'),
              onlinePurchaseURL: Yup.string()
                .matches(
                  /(https:\/\/|http:\/\/|mailto:)+[a-zA-Z0-9-_]/,
                  'Online purchase url is invalid url'
                )
                .notRequired(),
              description: Yup.string().required('T&Cs is required'),
              quantity: Yup.number()
                .integer()
                .moreThan(
                  initialValues.unlimitedQuantity ? -2 : -1,
                  'Quantity must be greater than or equal to 0'
                ),
              startOfDateTime: Yup.date()
                .nullable()
                .required('Start date and time is required')
                .test(
                  'is-before-end',
                  'Start Date Time must be earlier than End Date Time. Try again.',
                  function (value) {
                    const {endOfDateTime} = this.parent;
                    return !endOfDateTime || value <= endOfDateTime;
                  }
                ),
              endOfDateTime: Yup.date()
                .nullable()
                .required('End date and time is required')
                .test(
                  'is-after-start',
                  'End Date Time must be later than Start Date Time. Try again.',
                  function (value) {
                    const {startOfDateTime} = this.parent;
                    return !startOfDateTime || value >= startOfDateTime;
                  }
                ),
              quantityMaxPerRedemption: Yup.number().when('usageType', {
                is: 2,
                then: Yup.number()
                  .required('Quantity max per redemption is required')
                  .max(limitQuantity, `Maximum quantity is ${limitQuantity}.`),
                otherwise: Yup.number().nullable(),
              }),
              amountOffset: Yup.number().when('isCashoutAllowed', {
                is: true,
                then: Yup.number().min(
                  1,
                  'To allow reimbursement, voucher cash value cannot be zero.'
                ),
              }),
            })}
            onSubmit={addNewVoucher}
          >
            {({
              errors,
              handleBlur,
              handleChange,
              setFieldValue,
              handleSubmit,
              touched,
              values,
            }) => {
              // console.log('values', values);
              // console.log('errors', errors);
              // console.log('touched', touched);
              return (
                <form noValidate onSubmit={handleSubmit}>
                  <Grid container spacing={3}>
                    <Grid item md={12}>
                      <FormControl fullWidth margin="dense">
                        <Typography variant="body2" mb={2} fontWeight="bold">
                          Voucher Title (40 Max Char.){' '}
                          <span className="mandatory-label">(*)</span>
                        </Typography>
                        <Input
                          type="text"
                          name="title"
                          placeholder="Enter Voucher Title"
                          size="small"
                          variant="outlined"
                          value={values.title}
                          onChange={handleChange}
                          helperText={touched.title && errors.title}
                          onBlur={handleBlur}
                          error={Boolean(touched.title && errors.title)}
                        />
                      </FormControl>
                    </Grid>
                    <Grid item md={12}>
                      <FormControl fullWidth margin="dense">
                        <Typography variant="body2" mb={2} fontWeight="bold">
                          Voucher Short Description (85 Max Char.){' '}
                          <span className="mandatory-label">(*)</span>
                        </Typography>
                        <TextArea
                          label=""
                          placeholder="Enter Voucher Short Description"
                          maxRows={2}
                          name="excerpt"
                          value={values.excerpt}
                          helperText={touched.excerpt && errors.excerpt}
                          onBlur={handleBlur}
                          onChange={handleChange}
                          error={Boolean(touched.excerpt && errors.excerpt)}
                        />
                      </FormControl>
                    </Grid>
                    <Grid item md={4}>
                      <FormControl fullWidth margin="dense">
                        <Typography variant="body2" mb={2} fontWeight="bold">
                          Points Required
                        </Typography>
                        <Input
                          placeholder="Enter Points"
                          size="small"
                          name="cost"
                          type="number"
                          value={values.cost}
                          variant="outlined"
                          helperText={touched.cost && errors.cost}
                          onBlur={handleBlur}
                          onChange={handleChange}
                          error={Boolean(touched.cost && errors.cost)}
                        />
                      </FormControl>
                    </Grid>
                    <Grid item md={4}>
                      <FormControl fullWidth margin="dense">
                        <Typography variant="body2" mb={2} fontWeight="bold">
                          Voucher Cash Value
                        </Typography>
                        <Input
                          placeholder="Enter Amount Used"
                          size="small"
                          name="amountOffset"
                          type="number"
                          value={values.amountOffset}
                          variant="outlined"
                          helperText={
                            touched.amountOffset && errors.amountOffset
                          }
                          onBlur={handleBlur}
                          onChange={handleChange}
                          error={Boolean(
                            touched.amountOffset && errors.amountOffset
                          )}
                        />
                      </FormControl>
                    </Grid>
                    <Grid item md={4}>
                      <FormControl fullWidth margin="dense">
                        <div>
                          <Typography variant="body2" fontWeight="bold">
                            Allow reimbursement
                          </Typography>
                          <Checkbox
                            sx={{
                              marginLeft: -3,
                            }}
                            checked={values.isCashoutAllowed ? true : false}
                            name="isCashoutAllowed"
                            value={values.isCashoutAllowed}
                            onChange={(e, newValue) => {
                              setFieldValue('isCashoutAllowed', newValue);
                            }}
                            inputProps={{'aria-label': 'controlled'}}
                          />
                        </div>
                      </FormControl>
                    </Grid>
                    <Grid item md={4}>
                      <FormControl fullWidth margin="dense">
                        <Typography variant="body2" mb={2} fontWeight="bold">
                          Qty
                        </Typography>
                        <Input
                          placeholder="Enter Usable Quantity"
                          size="small"
                          name="quantity"
                          type="number"
                          value={values.quantity}
                          variant="outlined"
                          helperText={touched.quantity && errors.quantity}
                          onBlur={handleBlur}
                          onChange={handleChange}
                          error={Boolean(touched.quantity && errors.quantity)}
                          disabled={disabledQty}
                        />
                      </FormControl>
                    </Grid>
                    <Grid item md={4}>
                      <FormControl fullWidth margin="dense">
                        <div>
                          <Typography variant="body2" fontWeight="bold">
                            Unlimited Qty
                          </Typography>
                          <Checkbox
                            sx={{
                              marginLeft: -3,
                            }}
                            name="unlimitedQuantity"
                            checked={values.unlimitedQuantity}
                            value={values.unlimitedQuantity}
                            onChange={e => {
                              handleChange(e);
                              setInitialValues({
                                ...values,
                                quantity: e.target.checked ? -1 : 0,
                                unlimitedQuantity: e.target.checked,
                              });
                            }}
                            inputProps={{'aria-label': 'controlled'}}
                          />
                        </div>
                      </FormControl>
                    </Grid>
                    <Grid item md={12}>
                      <FormControl fullWidth>
                        <Typography variant="body2" mb={2} fontWeight="bold">
                          T&amp;Cs <span className="mandatory-label">(*)</span>
                        </Typography>
                        <RichTextEditor
                          label="Enter T&amp;Cs"
                          helperText={touched.description && errors.description}
                          error={Boolean(
                            touched.description && errors.description
                          )}
                          defaultValue={defaultValue}
                          controls={[
                            'title',
                            'bold',
                            'italic',
                            'underline',
                            'strikethrough',
                            'undo',
                            'redo',
                            'link',
                            'media',
                            'numberList',
                            'bulletList',
                            'source',
                          ]}
                          customControls={[
                            {
                              name: 'source',
                              icon: <SourceOutlinedIcon />,
                              type: 'callback',
                              onClick: () => {
                                onClickSource(initialValues?.description);
                              },
                            },
                          ]}
                          onChange={e => onChange(e, values, handleChange)}
                        />
                      </FormControl>
                    </Grid>
                    <Grid item md={12}>
                      <FormControl fullWidth margin="dense">
                        <Typography variant="body2" mb={2} fontWeight="bold">
                          Online Purchase URL
                        </Typography>
                        <Input
                          type="text"
                          name="onlinePurchaseURL"
                          placeholder="Enter Online Purchase URL"
                          size="small"
                          variant="outlined"
                          value={values.onlinePurchaseURL}
                          onChange={handleChange}
                          helperText={
                            touched.onlinePurchaseURL &&
                            errors.onlinePurchaseURL
                          }
                          onBlur={handleBlur}
                          error={Boolean(
                            touched.onlinePurchaseURL &&
                              errors.onlinePurchaseURL
                          )}
                        />
                      </FormControl>
                    </Grid>
                    <Grid item md={6}>
                      <FormControl variant="standard" fullWidth>
                        <Typography
                          variant="body2"
                          mb={2}
                          mt={3}
                          fontWeight="bold"
                        >
                          Start Date Time{' '}
                          <span className="mandatory-label">(*)</span>
                        </Typography>
                        <DateTimePicker
                          value={values.startOfDateTime}
                          maxDate={values?.endOfDateTime || undefined}
                          helperText={
                            touched.startOfDateTime && errors.startOfDateTime
                          }
                          isErrorMessage={Boolean(
                            touched.startOfDateTime && errors.startOfDateTime
                          )}
                          onChange={e => {
                            setFieldValue('startOfDateTime', e ?? null);
                          }}
                        />
                      </FormControl>
                    </Grid>
                    <Grid item md={6}>
                      <FormControl variant="standard" fullWidth>
                        <Typography
                          variant="body2"
                          mb={2}
                          mt={3}
                          fontWeight="bold"
                        >
                          End Date Time{' '}
                          <span className="mandatory-label">(*)</span>
                        </Typography>
                        <DateTimePicker
                          disabled={!values.startOfDateTime}
                          value={values.endOfDateTime}
                          helperText={
                            touched.endOfDateTime && errors.endOfDateTime
                          }
                          isErrorMessage={Boolean(
                            touched.endOfDateTime && errors.endOfDateTime
                          )}
                          minDate={values?.startOfDateTime || new Date()}
                          onChange={e => {
                            setFieldValue('endOfDateTime', e ?? null);
                          }}
                        />
                      </FormControl>
                    </Grid>
                    <Grid item md={6} mt={2}>
                      <FormControl fullWidth>
                        <Typography variant="body2" mb={2} fontWeight="bold">
                          Select IP
                        </Typography>
                        <MultipleSelectCheckbox
                          placeholder={selectIPPlaceholder}
                          value={islandPartners}
                          onChange={(e, values) => {
                            setOnChangeMultipleSelect(values);
                          }}
                          options={optionIslandPartners}
                        />
                      </FormControl>
                    </Grid>
                    <Grid item md={6} mt={2}>
                      <FormControl variant="standard" fullWidth>
                        <Typography variant="body2" mb={2} fontWeight="bold">
                          Voucher Category
                        </Typography>
                        <MultipleSelectCheckbox
                          placeholder={selectCategoryPlaceholder}
                          value={categorySelectCheckbox}
                          onChange={(e, values) => {
                            setCategorySelectCheckbox(values);
                          }}
                          options={optionVoucherCategory}
                        />
                      </FormControl>
                    </Grid>
                    <Grid item md={12}>
                      <FormControl variant="standard" fullWidth>
                        <Typography variant="body2" mb={2} fontWeight="bold">
                          Usage Type
                        </Typography>
                        <RadioGroups
                          name="usageType"
                          options={Object.values(ticketUsageTypeRadioOptions)
                            .filter(
                              item =>
                                item.value !== TicketUsageTypeEnums.DEFAULT
                            )
                            .sort(
                              (a, b) =>
                                [
                                  TicketUsageTypeEnums.SINGLE_NON_REPEATABLE,
                                  TicketUsageTypeEnums.SINGLE_REPEATABLE,
                                  TicketUsageTypeEnums.MULTI_REDEMPTION,
                                ].indexOf(a.value) -
                                [
                                  TicketUsageTypeEnums.SINGLE_NON_REPEATABLE,
                                  TicketUsageTypeEnums.SINGLE_REPEATABLE,
                                  TicketUsageTypeEnums.MULTI_REDEMPTION,
                                ].indexOf(b.value)
                            )}
                          values={values.usageType}
                          onChange={e => {
                            handleChange(e);
                            const quantityMaxPerRedemption =
                              +e.target.value ===
                              TicketUsageTypeEnums.MULTI_REDEMPTION
                                ? 1
                                : null;
                            setFieldValue(
                              'quantityMaxPerRedemption',
                              quantityMaxPerRedemption
                            );
                            setInitialValues({
                              ...values,
                              usageType: +e.target.value,
                              quantityMaxPerRedemption:
                                quantityMaxPerRedemption,
                            });
                          }}
                        />
                      </FormControl>
                    </Grid>
                    {values.usageType ===
                      TicketUsageTypeEnums.MULTI_REDEMPTION && (
                      <Grid item md={12} mt={4}>
                        <FormControl variant="standard" fullWidth>
                          <Typography variant="body2" mb={2} fontWeight="bold">
                            Maximum quantity per redemption transaction{' '}
                            <span className="mandatory-label">(*)</span>
                          </Typography>
                          <Typography
                            variant="caption"
                            fontSize="11px"
                            marginTop="-4px"
                          >
                            Enter any quantity up to {limitQuantity}.
                          </Typography>
                          <Input
                            className="helperText__unsetMarginLeft short-field"
                            placeholder="e.g. 3"
                            type="number"
                            size="small"
                            name="quantityMaxPerRedemption"
                            value={values.quantityMaxPerRedemption}
                            variant="outlined"
                            helperText={
                              touched.quantityMaxPerRedemption &&
                              errors.quantityMaxPerRedemption
                            }
                            onChange={e => {
                              if (e.target.value <= 0) return;
                              handleChange(e);
                            }}
                            error={Boolean(
                              touched.quantityMaxPerRedemption &&
                                errors.quantityMaxPerRedemption
                            )}
                          />
                        </FormControl>
                      </Grid>
                    )}
                    <Grid item md={12}>
                      <FormControl variant="standard" fullWidth>
                        <Typography
                          variant="body2"
                          mb={2}
                          mt={3}
                          fontWeight="bold"
                        >
                          Visibility
                        </Typography>
                        <RadioGroups
                          name="targetedDistribution"
                          options={radioTicketVisibilityOptions}
                          values={values.targetedDistribution}
                          onChange={e => {
                            setFieldValue(
                              'targetedDistribution',
                              +e.target.value
                            );
                            setInitialValues({
                              ...values,
                              targetedDistribution: +e.target.value,
                            });
                          }}
                        />
                      </FormControl>
                    </Grid>
                    <Grid item xs={6}>
                      <FormControl variant="standard" fullWidth>
                        <Typography
                          variant="body2"
                          mb={2}
                          mt={3}
                          fontWeight="bold"
                        >
                          Voucher Image
                        </Typography>
                        <AspectSelectImage
                          defaultImages={voucherImages}
                          setDefaultImages={setVoucherImages}
                        />
                      </FormControl>
                    </Grid>
                    <Grid item md={12}>
                      <Tooltips
                        title="When this is checked, it will bump the voucher to the top of the voucher listing page in B2C"
                        arrow={false}
                      >
                        <FormControl fullWidth margin="dense">
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                            }}
                          >
                            <Checkbox
                              sx={{
                                marginLeft: -3,
                              }}
                              name="priority"
                              checked={values.priority}
                              value={values.priority}
                              onChange={e => {
                                handleChange(e);
                                setInitialValues({
                                  ...values,
                                  priority: e.target.checked,
                                });
                              }}
                              inputProps={{'aria-label': 'controlled'}}
                            />
                            <Typography variant="body2" fontWeight="bold">
                              Priority
                            </Typography>
                          </div>
                          <Typography
                            variant="caption"
                            fontSize="11px"
                            marginTop="-4px"
                            marginLeft="29px"
                          >
                            Bump voucher to the top of listing in B2C Web
                          </Typography>
                        </FormControl>
                      </Tooltips>
                    </Grid>
                    <Grid item md={12}>
                      <FormControl fullWidth margin="dense">
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          <Checkbox
                            sx={{
                              marginLeft: -3,
                            }}
                            name="isExclusive"
                            checked={values.isExclusive ? true : false}
                            value={values.isExclusive}
                            onChange={e => {
                              handleChange(e);
                              setInitialValues({
                                ...values,
                                isExclusive: e.target.checked ? 1 : 0,
                              });
                            }}
                            inputProps={{'aria-label': 'controlled'}}
                          />
                          <Typography variant="body2" fontWeight="bold">
                            Exclusive
                          </Typography>
                        </div>
                        <Typography
                          variant="caption"
                          fontSize="11px"
                          marginLeft="29px"
                          marginTop="-4px"
                        >
                          Voucher cannot be used with any other vouchers
                        </Typography>
                      </FormControl>
                    </Grid>
                  </Grid>
                  <Divider variant="middle" pt={8} />
                  <StyledModalActions>
                    <div className="btn-action-1">
                      <Button
                        variant="text"
                        type="button"
                        onClick={onCloseModal}
                        loading={isLoading}
                      >
                        Cancel
                      </Button>
                    </div>
                    <div className="btn-action-2">
                      <Button type="submit" loading={isLoading}>
                        Add Voucher
                      </Button>
                    </div>
                  </StyledModalActions>
                </form>
              );
            }}
          </Formik>
        </DialogContent>
      </>
    </Modal>
  );
}
export default ModalAddNewVoucher;
